/**
 * O cache e o fluxo, ligados um no outro (H02).
 *
 * ── Por que TanStack Query, e não um cache próprio ────────────────────────
 *
 * O que a H02 pede — *"cache e invalidação a partir das notificações"* — é
 * pequeno de descrever e cheio de detalhe: deduplicar chamadas simultâneas para
 * a mesma chave, invalidar por prefixo, cancelar ao desmontar, não rebuscar o
 * que ninguém está olhando e não piscar a tela ao rebuscar. Cada um é uma linha
 * de código e um mês de defeito sutil.
 *
 * A invalidação por prefixo é o que decide: `chavesAtingidasPor` devolve
 * `['inbox']` e isso precisa alcançar toda variação de filtro montada na tela.
 * Escrever isso à mão é reescrever a parte difícil da biblioteca.
 *
 * ── Um cuidado de vocabulário ─────────────────────────────────────────────
 *
 * O TanStack chama de `stale` o dado que passou do `staleTime` dele. A §14.3
 * chama de `stale` o dado de PROJEÇÃO que passou do limiar do servidor. São
 * coisas diferentes, e confundi-las produziria uma faixa de "dados defasados"
 * aparecendo por decisão de cache, sem nenhuma engine estar fora.
 *
 * A regra: o `stale` do TanStack nunca chega à tela. O que a interface mostra
 * vem do campo que a API manda, e neste código ele se chama **defasado**.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ErroDaApi, ErroDeRede } from '@cliqcrm/contracts';
import { abrirFluxo, type EstadoDoFluxo, type Fluxo } from './fluxo-sse';
import { chavesAtingidasPor, interpretarRecurso } from './chaves';
import { useAssinaturas } from './assinaturas';
import { useSessao } from '@/shared/sessao/contexto';
import { revendaAtual } from '@/shared/sessao/revenda';
import { tokenDaSessao } from '@/shared/sessao/token-da-sessao';

/**
 * Quantas vezes insistir, e em quê.
 *
 * `retryable` sai do catálogo de problemas (B34) e é a resposta do SERVIDOR
 * sobre se vale repetir. Insistir num 400 gastaria três chamadas para receber a
 * mesma recusa; insistir num 404 de recurso de outro tenant (§19) seria sondar.
 */
export function deveInsistir(tentativa: number, erro: unknown): boolean {
  /*
   * O limite de requisições (429) ganha uma tentativa a mais: ele passa
   * sozinho, e o servidor diz quando (ver `esperaAntesDeInsistir`). As outras
   * recusas que valem repetir param na segunda.
   */
  const teto = erro instanceof ErroDaApi && erro.codigo === 'LIMITE_EXCEDIDO' ? 3 : 2;
  if (tentativa >= teto) return false;
  if (erro instanceof ErroDeRede) return true;
  if (erro instanceof ErroDaApi) return erro.retryable;
  return false;
}

/**
 * Quanto esperar antes de tentar de novo.
 *
 * Quando o servidor diz (o `Retry-After` do 429), espera o que ele pediu, até
 * um minuto, com um pouco de folga aleatória para várias telas abertas não
 * voltarem todas no mesmo instante. Tentar antes disso é receber a mesma
 * recusa e queimar a tentativa que daria certo. Sem o cabeçalho, o recuo de
 * sempre: 1 s, 2 s, 4 s.
 */
export function esperaAntesDeInsistir(tentativa: number, erro: unknown): number {
  if (erro instanceof ErroDaApi && erro.esperarSegundos !== null) {
    const pedida = Math.min(60, erro.esperarSegundos) * 1000;
    return pedida + Math.floor(Math.random() * 500);
  }
  return Math.min(4_000, 500 * 2 ** tentativa);
}

export function criarClienteDeConsulta(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: deveInsistir,
        retryDelay: esperaAntesDeInsistir,

        /**
         * Zero: toda montagem rebusca.
         *
         * Parece agressivo e não é. Quem mantém o dado fresco aqui é o SSE, não
         * o relógio — e o que o `staleTime` faria de útil é evitar rebusca ao
         * voltar para uma tela. Só que "voltar para uma tela" é exatamente o
         * momento em que o usuário quer ver o estado atual.
         *
         * O custo é uma requisição barata; o benefício é nunca mostrar um
         * cartão que o SSE não alcançou porque o fluxo estava caído.
         */
        staleTime: 0,

        // Voltar para a aba rebusca: o navegador congela o fluxo em aba de
        // fundo, e voltar é o momento mais provável de haver o que perder.
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        // Comando NÃO repete sozinho. Sem `Idempotency-Key` (B30) uma segunda
        // tentativa pode enviar a mensagem duas vezes, e quem sabe se a chave
        // existe é quem chama — não o padrão.
        retry: false,
      },
    },
  });
}

// ── Estado do fluxo, observável ───────────────────────────────────────────
//
// Fora do React de propósito: o fluxo é único na aba e vive fora da árvore de
// componentes. Guardá-lo em contexto obrigaria o provedor a renderizar de novo
// a cada mudança de conexão, e com ele a aplicação inteira.

let estadoDoFluxo: EstadoDoFluxo = 'parado';
const ouvintesDoFluxo = new Set<() => void>();

function definirEstadoDoFluxo(estado: EstadoDoFluxo): void {
  if (estado === estadoDoFluxo) return;
  estadoDoFluxo = estado;
  for (const ouvinte of ouvintesDoFluxo) ouvinte();
}

/** O estado do fluxo, para a tela poder dizer que não está ao vivo (H03). */
export function useEstadoDoFluxo(): EstadoDoFluxo {
  return useSyncExternalStore(
    (ouvinte) => {
      ouvintesDoFluxo.add(ouvinte);
      return () => {
        ouvintesDoFluxo.delete(ouvinte);
      };
    },
    () => estadoDoFluxo,
    () => 'parado' as const,
  );
}

export function ProvedorDeDados({ children }: { children: ReactNode }): ReactNode {
  // `useState` com inicializador, e não `useMemo`: o React pode descartar um
  // `useMemo` quando quiser, e recriar o cliente jogaria fora o cache inteiro.
  const [cliente] = useState(criarClienteDeConsulta);

  return (
    <QueryClientProvider client={cliente}>
      <LigacaoDoFluxo />
      {children}
    </QueryClientProvider>
  );
}

/**
 * Não desenha nada: mantém o fluxo aberto e traduz notificação em invalidação.
 */
function LigacaoDoFluxo(): null {
  const cache = useQueryClient();
  const { assinaturasAtuais } = useAssinaturas();

  /*
   * A ref existe porque a lista muda a toda hora — cada troca de conversa a
   * muda — e o efeito que abre o fluxo não pode depender dela: dependesse, o
   * React derrubaria e recriaria a conexão a cada render.
   *
   * O que ela NÃO resolve, e por muito tempo pareceu resolver: o servidor
   * congela as assinaturas na abertura. Ler a ref na conexão seguinte só ajuda
   * quem reconecta. Quem abre uma conversa com o fluxo já de pé precisa que
   * alguém peça a reabertura — é o `renovar` do efeito abaixo.
   */
  const assinaturas = useRef(assinaturasAtuais);
  assinaturas.current = assinaturasAtuais;
  const fluxo = useRef<Fluxo | null>(null);

  // Numa ref pelo mesmo motivo: o efeito que abre o fluxo não depende dela.
  const { recarregarMe } = useSessao();
  const relerMe = useRef(recarregarMe);
  relerMe.current = recarregarMe;

  useEffect(() => {
    const aberto = abrirFluxo({
      assinaturas: () => assinaturas.current,
      /*
       * A MESMA função do cliente da API, e não uma cópia.
       *
       * Aqui havia uma cópia que só lia a credencial guardada, sem renovar. Ver
       * o cabeçalho de `token-da-sessao.ts`: foi o que deixou o tempo real fora
       * do ar por horas sem nada na tela dizer isso.
       */
      token: () => {
        const revenda = revendaAtual();
        if (!revenda) throw new Error('sem organização para abrir o fluxo');
        return tokenDaSessao(revenda);
      },
      aoMudar: (recurso) => {
        // `sessao:eu`: o `/v1/me` mudou, e ele mora na sessão, não no cache.
        if (interpretarRecurso(recurso)?.tipo === 'sessao') void relerMe.current();
        for (const chave of chavesAtingidasPor(recurso)) {
          void cache.invalidateQueries({ queryKey: chave });
        }
      },
      // Conectou ou reconectou: rebusca o que está montado. É a camada que
      // cobre o que o `Last-Event-ID` não alcança — ver `fluxo-sse.ts`. O
      // `/v1/me` entra junto: o aviso de sessão pode ter passado com o fluxo
      // caído, ou com a aba em segundo plano.
      aoRecuperar: () => {
        void cache.invalidateQueries({ type: 'active' });
        void relerMe.current();
      },
      aoEstado: definirEstadoDoFluxo,
    });

    fluxo.current = aberto;
    return () => {
      fluxo.current = null;
      aberto.encerrar();
    };
  }, [cache]);

  /*
   * A lista mudou: reabre com ela.
   *
   * A dependência é a lista JUNTA numa string, e não o array: `assinaturasAtuais`
   * nasce novo a cada render, e o array como dependência dispararia o efeito
   * sempre. `renovar` compara conteúdo e não faz nada quando é igual, então uma
   * chamada a mais é barata — mas uma por render seria desperdício visível no
   * perfil da tela.
   */
  const listaDeAssinaturas = assinaturasAtuais.join('|');
  useEffect(() => {
    fluxo.current?.renovar();
  }, [listaDeAssinaturas]);

  return null;
}

/** Invalidação manual, para depois de um comando. */
export function useInvalidar(): (chaves: readonly (readonly unknown[])[]) => void {
  const cache = useQueryClient();
  return useMemo(
    () => (lista) => {
      for (const chave of lista) void cache.invalidateQueries({ queryKey: chave });
    },
    [cache],
  );
}

export { useQueryClient };
